/**
 * @description Teste grátis de 7 dias (5.5). Admin global: lista e ficha dos cadastros feitos
 *              no site (/trial-leads). Usuário em teste: situação do teste, pregão de teste e
 *              lances simulados (/trial/me).
 */
import { http } from '@/chassis/data/services/http-client';

export type LeadStatus = 'active' | 'expired' | 'ended';
export type TagColor = 'roxo' | 'azul' | 'verde' | 'amarelo' | 'laranja' | 'vermelho';

export interface TrialWindow {
    startedAt: string;
    endsAt: string;
    totalDays: number;
    daysUsed: number;
    daysLeft: number;
    expired: boolean;
}

export interface LeadTag {
    label: string;
    color: TagColor;
    until: string;
    setAt?: string;
    setBy?: string;
}

export interface LeadContact {
    date: string;
    time: string;
    channel: 'whatsapp' | 'ligacao' | 'videochamada';
}

export interface LeadNote {
    id: string;
    kind: 'note' | 'contact' | 'tag' | 'extend' | 'end';
    text: string;
    at: string;
    byName?: string;
}

export interface TrialLeadListItem {
    id: string;
    name: string;
    email: string;
    phone: string;
    city: string;
    state: string;
    agencyName: string;
    roleLabel: string;
    contact: LeadContact | null;
    createdAt: string;
    status: LeadStatus;
    trial: TrialWindow;
    contacted: boolean;
    contactedAt: string | null;
    tag: LeadTag | null;
    tagActive: boolean;
    lastNote: LeadNote | null;
    extensions: number;
}

export interface TrialLeadTotals {
    all: number;
    active: number;
    endingSoon: number;
    expired: number;
    notContacted: number;
    tagged: number;
}

export interface SandboxTender {
    id: string;
    title: string;
    status: string;
    publicTenderCode: string;
    createdAt: string;
}

export interface TrialLeadDetail extends Omit<TrialLeadListItem, 'lastNote' | 'extensions'> {
    cpf: string;
    cityCode: string | null;
    role: string;
    source: string;
    notes: LeadNote[];
    extensions: Array<{ previousEndsAt: string; newEndsAt: string; at: string; byName?: string }>;
    contactedBy?: string | null;
    endedAt?: string | null;
    usage: {
        tenders: SandboxTender[];
        internalPhaseProjects: number;
        loginCount: number;
        lastLoginAt: string | null;
    } | null;
}

export interface MyTrial extends TrialWindow {
    leadId: string;
    name: string;
    agencyName: string;
    roleLabel: string;
    contact: LeadContact | null;
    status: LeadStatus;
    tenders: SandboxTender[];
}

export interface TestTenderResult {
    tenderUuid: string;
    tenderCode: string;
    proposalsCreated: number;
    schedule: { proposalEnd: string; disputeStart: string } | null;
    redirectPath: string;
}

export const trialService = {
    listLeads: (query: { status?: string; filtro?: string; q?: string } = {}) =>
        http.get<{ items: TrialLeadListItem[]; totals: TrialLeadTotals }>('/trial-leads', query),
    getLead: (id: string) => http.get<TrialLeadDetail>(`/trial-leads/${id}`),
    setContacted: (id: string, contacted: boolean, note?: string) => http.patch<TrialLeadDetail>(`/trial-leads/${id}/contact`, { contacted, note }),
    addNote: (id: string, text: string) => http.post<TrialLeadDetail>(`/trial-leads/${id}/notes`, { text }),
    setTag: (id: string, tag: { label: string; color: TagColor; until: string }) => http.put<TrialLeadDetail>(`/trial-leads/${id}/tag`, tag),
    removeTag: (id: string) => http.delete<TrialLeadDetail>(`/trial-leads/${id}/tag`),
    extend: (id: string, input: { days?: number; until?: string; note?: string }) => http.post<TrialLeadDetail>(`/trial-leads/${id}/extend`, input),
    end: (id: string, note?: string) => http.post<TrialLeadDetail>(`/trial-leads/${id}/end`, { note }),

    me: () => http.get<{ trial: MyTrial | null }>('/trial/me'),
    createTestTender: () => http.post<TestTenderResult>('/trial/me/test-tender', {}),
    simulateBids: (tenderId: string) =>
        http.post<{ bidsCreated: number; lots: number; rejected?: Array<{ lot: string; reason: string }> }>('/trial/me/simulate-bids', { tenderId }),
};

export const CHANNEL_LABEL: Record<LeadContact['channel'], string> = {
    whatsapp: 'WhatsApp',
    ligacao: 'Ligação',
    videochamada: 'Videochamada',
};

export const STATUS_LABEL: Record<LeadStatus, string> = {
    active: 'Em teste',
    expired: 'Teste vencido',
    ended: 'Encerrado',
};

export const TAG_COLORS: TagColor[] = ['roxo', 'azul', 'verde', 'amarelo', 'laranja', 'vermelho'];

/** YYYY-MM-DD -> dd/mm/aaaa (sem passar por Date, para não mudar o dia pelo fuso). */
export function ymdToBr(ymd?: string | null): string {
    if (!ymd || !/^\d{4}-\d{2}-\d{2}/.test(ymd)) return 'Não informado';
    const [y, m, d] = ymd.slice(0, 10).split('-');
    return `${d}/${m}/${y}`;
}

export function isoToBr(iso?: string | null, withTime = false): string {
    if (!iso) return 'Não informado';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return 'Não informado';
    return d.toLocaleString('pt-BR', withTime
        ? { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'America/Sao_Paulo' }
        : { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'America/Sao_Paulo' });
}

export function phoneBr(digits?: string | null): string {
    const d = String(digits || '').replace(/\D/g, '');
    if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
    if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
    return d || 'Não informado';
}

export function errorMessage(err: unknown, fallback: string): string {
    const e = err as { message?: string; details?: { message?: string } };
    return e?.details?.message || e?.message || fallback;
}
